/**
 * Crosstab機能の型定義（仕様書 v2.0準拠）
 */

import type { ColumnId, DatasetId } from '../../../types/id.types';
import type { DataType } from '../../../types/core.types';
import type { ColorSchemeName } from './colorSchemes';

// =============================================================================
// Constants
// =============================================================================

/**
 * レコード数カウント用の特殊フィールドID
 * 特定のフィールドに依存せず、レコード数を集計する際に使用
 */
export const COUNT_FIELD_ID = '__count__';

// =============================================================================
// Aggregation Types
// =============================================================================

export type AggregationFunction = 'sum' | 'avg' | 'count' | 'min' | 'max';

// =============================================================================
// Configuration Types
// =============================================================================

/**
 * フィールド記述子
 */
export interface FieldDescriptor {
  fieldId: ColumnId;
  fieldName: string;
  dataType: DataType;
  sortOrder?: 'asc' | 'desc' | 'data' | 'freq';
}

/**
 * 値フィールド記述子
 */
export interface ValueFieldDescriptor {
  fieldId?: ColumnId; // Countのみの場合はundefined（COUNT_FIELD_IDを使用）
  fieldName: string;
  aggregation: AggregationFunction;
  alias?: string; // ユーザー指定のカスタム名
  dataType: DataType; // 集計後の型
}

/**
 * Crosstabオプション
 */
export interface CrosstabOptions {
  showRowTotals: boolean;
  showColumnTotals: boolean;
  showGrandTotal: boolean;
  emptyDisplayMode: 'blank' | 'zero' | 'dash' | 'na';
  percentageMode: 'none' | 'row' | 'column' | 'total' | 'all';
  visualMode: 'table' | 'heatmap' | 'text-heatmap' | 'bar-in-cell';
  colorScheme?: ColorSchemeName;
}

/**
 * Crosstab設定
 */
export interface CrosstabConfiguration {
  rowFields: FieldDescriptor[];
  columnFields: FieldDescriptor[];
  valueFields: ValueFieldDescriptor[];
  options: CrosstabOptions;
}

// =============================================================================
// Structure Types
// =============================================================================

/**
 * 行ヘッダー
 */
export interface RowHeader {
  key: RowKey; // 内部キー（JSON形式）
  displayValues: string[]; // 表示用の値
  level: number; // 階層レベル（0が最上位）
  parentKey?: RowKey; // 親グループのキー
  isSubtotal: boolean; // 小計行かどうか
}

/**
 * 列ヘッダー
 */
export interface ColumnHeader {
  key: ColumnKey; // 内部キー（JSON形式）
  displayValues: string[]; // 表示用の値（Column値）
  level: number; // 階層レベル（0が最上位）
  parentKey?: ColumnKey; // 親グループのキー
  isSubtotal: boolean; // 小計列かどうか
  // Column × Measure Pivot用（複数値フィールドの場合のみ使用）
  measureFieldName?: string; // Measureフィールド名（例: "Sales"）
  measureFieldAlias?: string; // Measureのエイリアス
  measureAggregation?: AggregationFunction; // 集約関数（例: "sum"）
  measureIndex: number; // values配列のインデックス（0, 1, 2...）
}

/**
 * 階層ノード（複数フィールド時の階層構造）
 */
export interface HierarchyNode {
  key: string;
  value: string;
  children?: HierarchyNode[];
}

/**
 * セル値
 */
export interface CellValue {
  values: number[]; // 複数値フィールドがある場合、配列
  percentages?: {
    row?: (number | undefined)[]; // AVG/MIN/MAXの場合undefinedになる
    column?: (number | undefined)[];
    total?: (number | undefined)[];
  };
  recordCount: number; // このセルを構成するレコード数
  isEmpty: boolean; // データが存在しないセルかどうか
}

// =============================================================================
// Key Types
// =============================================================================

/**
 * 行キー（JSON形式）
 * 例: '["North","2024-01"]' or '["North_America",null]'
 */
export type RowKey = string;

/**
 * 列キー（JSON形式）
 * 例: '["Product A"]' or '["Electronics","Laptop"]'
 */
export type ColumnKey = string;

/**
 * セルキー
 * 形式: "row:<rowKey>:col:<colKey>"
 */
export type CellKey = string;

// =============================================================================
// Main Data Model
// =============================================================================

/**
 * Crosstabデータモデル（仕様書準拠）
 */
export interface CrosstabDataModel {
  // メタデータ
  metadata: {
    datasetId: DatasetId;
    generatedAt: string; // ISO timestamp
    version: string; // "2.0"
  };

  // フィールド設定
  configuration: CrosstabConfiguration;

  // 行と列の構造
  structure: {
    rowHeaders: RowHeader[];
    columnHeaders: ColumnHeader[];
    rowHierarchy?: HierarchyNode[]; // 複数行フィールド時の階層構造
    columnHierarchy?: HierarchyNode[]; // 複数列フィールド時の階層構造
  };

  // 実際のデータ
  data: {
    cells: Map<CellKey, CellValue>;
    rowTotals?: Map<RowKey, CellValue>;
    columnTotals?: Map<ColumnKey, CellValue>;
    grandTotal?: CellValue;
  };

  // 元データへのリンク
  cellToRecords: Map<CellKey, number[]>; // セルから元データの行番号へのマッピング
}

// =============================================================================
// Tab Configuration
// =============================================================================

/**
 * ソート方式
 */
export type SortMethod = 'label-asc' | 'label-desc' | 'value';

/**
 * 値ソートの順序
 */
export type SortOrder = 'desc' | 'asc'; // desc = High to Low, asc = Low to High

/**
 * ソート設定（行または列）
 */
export interface SortSetting {
  method: SortMethod;
  sortByKey?: string; // valueソート時にどの列/行でソートするか（'total' or 列/行のキー）
  order?: SortOrder; // valueソート時の順序
}

/**
 * ソート設定
 */
export interface SortConfig {
  rows?: SortSetting;
  columns?: SortSetting;
}

/**
 * Crosstabタブの設定（タブに保存される状態）
 *
 * CrosstabOperationと類似の構造を持つが、タブUI用に以下の違いがある:
 * - datasetId（Operationではtargetとして使用）
 * - idとtypeは不要（タブの操作IDはexecuteOperation呼び出し時に生成）
 * - tabStateにUI専用の状態を格納
 */
export interface CrosstabTabConfig {
  datasetId?: DatasetId;
  rowFields: string[]; // フィールド名の配列
  columnFields: string[];
  valueFields: {
    field: string;
    aggregation: AggregationFunction;
    alias?: string;
  }[];
  options: CrosstabOptions;
  sortConfig?: SortConfig; // ソート設定
  // UI専用状態
  tabState?: {
    cachedResult?: CrosstabDataModel; // 最後に生成した結果（pane split時も保持）
  };

  /**
   * @deprecated tabState.cachedResultを使用してください
   */
  cachedResult?: CrosstabDataModel;
}

// =============================================================================
// Selection State
// =============================================================================

/**
 * セル選択状態
 */
export interface CellSelectionState {
  totalRecords: number; // このセルに含まれる全レコード数
  selectedRecords: number; // 現在選択されているレコード数
  selectionRatio: number; // 選択率 (selectedRecords / totalRecords)
}

/**
 * 選択状態の種類
 */
export type SelectionStatus = 'unselected' | 'partial' | 'full';

// =============================================================================
// Utility Functions (型ガード等)
// =============================================================================

/**
 * 選択状態を判定
 */
export function getCellSelectionStatus(ratio: number): SelectionStatus {
  if (ratio === 0) return 'unselected';
  if (ratio === 1) return 'full';
  return 'partial';
}
